import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DateTime } from "luxon";
import { useState } from "react";
import { FieldValues } from "react-hook-form";
import type { DialogProps } from "../misc/DialogProps";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { LabeledBox } from "../misc/LabeledBox";
import { Form2 } from "../misc/Form2";
import { DateInput } from "../inputs/DateInput";
import { SaveButton2 } from "../buttons/SaveButton2";
import { cancelDunningProcess, clearDunningProcessPostponement } from "../api/backend.api";
import { createNote } from "../api/backoffice.api";
import { Student, studentsProvider } from "../providers/studentsProvider";
import { formatDate } from "../utils/calendar";
import { getDunningStatusLabel } from "../utils/dunning";
import { useDialog } from "../hooks/useDialog";
import { useRefresh } from "react-admin";
import { InkassoBlockingDialog } from "./InkassoBlockingDialog";
import { grants } from "../backoffice.access_control";

export function StudentDunningStatusDialog(
  props: DialogProps & { student: Student; dunningDisabled: boolean; dunningPostponedUntil?: DateTime | null },
) {
  const { student, dunningDisabled, dunningPostponedUntil, onClose, ...dialogProps } = props;
  const isAutovioAdmin = grants.has("admin");
  const {
    dialogProps: reasonDialogProps,
    isDialogOpen: isReasonDialogOpen,
    openDialog: openReasonDialog,
    closeDialog: closeReasonDialog,
  } = useDialog();
  const [reasonDialogAction, setReasonDialogAction] = useState<"temporarilyDisable" | "disable">("disable");
  const [reason, setReason] = useState("");
  const [pendingDunningDisabledUntil, setPendingDunningDisabledUntil] = useState<DateTime | null>(null);
  const [savingReason, setSavingReason] = useState(false);
  const [openCollectionsInfoDialog, setOpenCollectionsInfoDialog] = useState(false);
  const refresh = useRefresh();
  const reasonDialogMode = isReasonDialogOpen ? reasonDialogAction : null;
  const effectiveDunningPostponedUntil = _getEffectiveDunningPostponedUntil(student, dunningPostponedUntil);
  const isInCollections = student.dunningProcess?.status === "handedOverToPairFinance";
  const isTemporarilyPostponed = !!effectiveDunningPostponedUntil && effectiveDunningPostponedUntil > DateTime.now();
  const statusLabel = getDunningStatusLabel(dunningDisabled, effectiveDunningPostponedUntil);

  const openTemporaryDunningDisableReasonDialog = (data: FieldValues) => {
    if (dunningDisabled) {
      return;
    }
    const dunningDisabledUntil = data.dunningDisabledUntil as DateTime;
    setPendingDunningDisabledUntil(dunningDisabledUntil);
    setReason("");
    setReasonDialogAction("temporarilyDisable");
    openReasonDialog();
  };

  const submitTemporaryDunningDisableWithReason = async (dunningDisabledUntil: DateTime, reasonText: string) => {
    await runSafely(
      async () => {
        await studentsProvider.update("students", {
          id: student.id,
          previousData: student,
          data: { dunningPostponedUntil: dunningDisabledUntil },
        });
        try {
          await createNote({
            studentUid: student.id,
            body: `🛑 Mahnwesen zeitweise deaktiviert bis ${formatDate(dunningDisabledUntil)}.\nGrund: ${reasonText}`,
          });
        } catch (error) {
          handleError("Fehler beim Erstellen der Notiz.", error);
        }
      },
      {
        errorMessage: "Fehler beim Speichern",
        successMessage: `Das Mahnwesen für ${student.name} wurde bis ${formatDate(dunningDisabledUntil)} deaktiviert.`,
        onSuccess: () => {
          closeReasonDialogAndReset();
          refresh();
          onClose();
        },
      },
    );
  };

  const activateDunning = async () => {
    await runSafely(
      async () => {
        await studentsProvider.update("students", {
          id: student.id,
          previousData: student,
          data: { dunningDisabled: false, dunningPostponedUntil: null },
        });
        if (student.dunningProcess?.id) {
          try {
            await clearDunningProcessPostponement(student.dunningProcess.id, student.id);
          } catch (error) {
            console.warn("Failed to clear dunning process postponement during activation", error);
          }
        }
        try {
          await createNote({
            studentUid: student.id,
            body: "✅ Mahnwesen aktiviert.",
          });
        } catch (error) {
          console.warn("Failed to create activation note, but activation succeeded", error);
        }
      },
      {
        errorMessage: `Fehler beim Aktivieren des Mahnwesens.`,
        successMessage: `Das Mahnwesen für ${student.name} wurde aktiviert.`,
        onSuccess: () => {
          refresh();
          onClose();
        },
      },
    );
  };

  const submitDisableWithReason = async (reasonText: string) => {
    await runSafely(
      async () => {
        await permanentlyDisableDunning(student, reasonText);
      },
      {
        errorMessage: `Fehler beim Deaktivieren des Mahnwesens.`,
        successMessage: `Das Mahnwesen für ${student.name} wurde deaktiviert.`,
        onSuccess: () => {
          closeReasonDialogAndReset();
          refresh();
          onClose();
        },
      },
    );
  };

  const closeReasonDialogAndReset = () => {
    closeReasonDialog();
    setReason("");
    setPendingDunningDisabledUntil(null);
    setSavingReason(false);
  };

  const saveReason = async () => {
    if (!reason.trim()) {
      showErrorToast("Bitte gib einen Grund für die Ausblendung an.");
      return;
    }
    setSavingReason(true);
    if (reasonDialogMode === "temporarilyDisable") {
      if (!pendingDunningDisabledUntil) {
        showErrorToast("Bitte wähle ein Datum aus.");
        setSavingReason(false);
        return;
      }
      await submitTemporaryDunningDisableWithReason(pendingDunningDisabledUntil, reason.trim());
      setSavingReason(false);
      return;
    }
    if (reasonDialogMode === "disable") {
      await submitDisableWithReason(reason.trim());
      setSavingReason(false);
      return;
    }
    setSavingReason(false);
  };

  return (
    <Dialog {...dialogProps} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Automatisches Mahnwesen</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Alert severity={dunningDisabled ? "warning" : isTemporarilyPostponed ? "warning" : "info"} sx={{ mt: 1 }}>
          <Typography variant="body2">Status: {statusLabel}</Typography>
        </Alert>
        <Typography sx={{ mt: 3 }}>
          Hierüber kannst du das Mahnwesen von AUTOVIO für einzelne Fahrschüler deaktivieren und aktivieren.
        </Typography>
        <Typography sx={{ mt: 2 }}>
          Deaktivierst du das Mahnwesen für einen Schüler, übernimmt AUTOVIO nichts mehr bei offenen Zahlungen.
        </Typography>
        {isAutovioAdmin && (
          <LabeledBox label="Mahnwesen zeitweise deaktivieren" sx={{ mt: 4 }} fullWidth>
            <Form2
              onSubmit={openTemporaryDunningDisableReasonDialog}
              defaultValues={{
                dunningDisabledUntil: effectiveDunningPostponedUntil ?? DateTime.now().plus({ days: 7 }),
              }}
            >
              <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="body2">
                  Hier kannst du das Mahnwesen für einen Schüler bis zu einem bestimmten Datum deaktivieren.
                </Typography>
                <DateInput
                  source="dunningDisabledUntil"
                  label="Deaktiviert bis"
                  helperText={
                    dunningDisabled
                      ? "Solange das Mahnwesen deaktiviert ist, kann kein Zeitraum gesetzt werden."
                      : "Wähle das Datum, bis zu dem das Mahnwesen für den Fahrschüler deaktiviert bleiben soll."
                  }
                  disabled={dunningDisabled}
                />
              </Box>
              <DialogActions>
                <SaveButton2 label="Mahnwesen zeitweise deaktivieren" disabled={dunningDisabled} />
              </DialogActions>
            </Form2>
          </LabeledBox>
        )}
        <LabeledBox label="Komplett aus dem Mahnwesen nehmen" sx={{ mt: 4 }} fullWidth>
          <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2">
              Diese Einstellung entfernt den Fahrschüler vollständig aus dem Mahnwesen, bis du ihn wieder aktivierst.
            </Typography>
            {dunningDisabled && (
              <Alert severity="warning">
                <Typography variant="body2">
                  Das Mahnwesen ist aktuell deaktiviert. Temporäre Ausblendungen greifen erst wieder nach einer
                  Reaktivierung.
                </Typography>
              </Alert>
            )}
          </Box>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => {
                const action = getDisableActionType({ dunningDisabled, isInCollections });
                if (action === "activate") {
                  void activateDunning();
                  return;
                }
                if (action === "showCollectionsInfoDialog") {
                  setOpenCollectionsInfoDialog(true);
                  return;
                }
                setReason("");
                setReasonDialogAction("disable");
                openReasonDialog();
              }}
            >
              {dunningDisabled ? "Mahnwesen aktivieren" : "Mahnwesen deaktivieren"}
            </Button>
          </DialogActions>
        </LabeledBox>
      </DialogContent>
      <DialogActions />

      <Dialog {...reasonDialogProps} onClose={closeReasonDialogAndReset} maxWidth="sm" fullWidth>
        <DialogTitle>Bitte Grund angeben</DialogTitle>
        <DialogCloseButton onClick={closeReasonDialogAndReset} />
        <DialogContent>
          <TextField
            autoFocus
            label="Grund"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            sx={{ mt: 1 }}
            helperText="Der Grund wird als Notiz beim Fahrschüler gespeichert."
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeReasonDialogAndReset}>
            Abbrechen
          </Button>
          <LoadingButton variant="contained" loading={savingReason} onClick={() => void saveReason()}>
            Speichern
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <InkassoBlockingDialog open={openCollectionsInfoDialog} onClose={() => setOpenCollectionsInfoDialog(false)} />
    </Dialog>
  );
}

function _getEffectiveDunningPostponedUntil(
  student: Student,
  dunningPostponedUntil?: DateTime | null,
): DateTime | undefined {
  return dunningPostponedUntil ?? student.dunningPostponedUntil ?? undefined;
}

export async function permanentlyDisableDunning(student: Student, reasonText: string): Promise<void> {
  const dunningProcessId = student.dunningProcess?.id;
  if (dunningProcessId) {
    await cancelDunningProcess(dunningProcessId);
  }
  await studentsProvider.update("students", {
    id: student.id,
    previousData: student,
    data: { dunningDisabled: true, dunningPostponedUntil: null },
  });
  // Create note, but don't block if it fails
  try {
    await createNote({
      studentUid: student.id,
      body: `🛑 Mahnwesen deaktiviert.\nGrund: ${reasonText}`,
    });
  } catch (noteError) {
    handleError("Fehler beim Erstellen der Notiz.", noteError);
  }
}

export type DisableActionType = "activate" | "showCollectionsInfoDialog" | "openDisableReasonDialog";

export function getDisableActionType({
  dunningDisabled,
  isInCollections,
}: {
  dunningDisabled: boolean;
  isInCollections: boolean;
}): DisableActionType {
  if (dunningDisabled) {
    return "activate";
  }
  if (isInCollections) {
    return "showCollectionsInfoDialog";
  }
  return "openDisableReasonDialog";
}
